import { useState } from "react";
import { ChevronDown, ChevronLeft } from "lucide-react";
import { Button } from "./ui/button";
import { useLanguage } from "../context/LanguageContext";

/**
 * CommentaryAccordion - Two-tier collapsible system for organizing many commentators
 * 
 * Tier 1: Categories (e.g., "ראשונים", "אחרונים", "תרגומים")
 * Tier 2: Individual commentators within each category
 * 
 * Only shows commentators that have content for the current verse.
 * Only one commentator's text is expanded at a time to prevent endless scrolling.
 */

// Group commentators by period
const CATEGORY_MAP = {
  rishonim: { he: "ראשונים", en: "Rishonim · Early Commentators", order: 1 },
  acharonim: { he: "אחרונים", en: "Acharonim · Later Commentators", order: 2 },
  targumim:  { he: "תרגומים", en: "Targumim · Aramaic Translations", order: 3 },
};

function getCategoryForPeriod(period, type) {
  if (type === "translation") return "targumim";
  if (period === "Rishonim" || period === "Tannaim" || period === "Amoraim") return "rishonim";
  return "acharonim";
}

export default function CommentaryAccordion({
  commentaries,        // Array of {commentator, commentator_he, he, en, period, type}
  activeCommentator,   // Currently expanded commentator name (or null)
  onToggle,           // (name: string) => void
}) {
  const { isEn } = useLanguage();
  const [openCategories, setOpenCategories] = useState(new Set(["rishonim"])); // Start with Rishonim open

  // Group commentaries by category
  const grouped = {};
  commentaries.forEach((comm) => {
    const catKey = getCategoryForPeriod(comm.period, comm.type);
    if (!grouped[catKey]) grouped[catKey] = [];
    // Pick display text: in English mode prefer the English translation
    // when Sefaria has one; fall back to Hebrew with the existing
    // "translation not yet available" affordance further down.
    const hasEn = !!(comm.en && comm.en.trim());
    grouped[catKey].push({
      name: comm.commentator,
      he: comm.commentator_he,
      en: comm.commentator,        // Sefaria's canonical English name (e.g. "Rashi", "Ramban")
      text: isEn
        ? (hasEn ? comm.en : (comm.he || ""))
        : (comm.he || comm.en || ""),
      hasEnglish: hasEn,
      period: comm.period,
      type: comm.type,
    });
  });

  // Sort categories by order
  const sortedCategories = Object.entries(grouped).sort(
    ([a], [b]) => CATEGORY_MAP[a].order - CATEGORY_MAP[b].order
  );

  const toggleCategory = (catKey) => {
    setOpenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(catKey)) {
        next.delete(catKey);
      } else {
        next.add(catKey);
      }
      return next;
    });
  };

  if (commentaries.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground text-sm">
        {isEn ? "No commentary available for this verse." : "אין פרשנויות זמינות לפסוק זה"}
      </div>
    );
  }

  return (
    <div className="space-y-2" dir={isEn ? "ltr" : "rtl"}>
      {sortedCategories.map(([catKey, comms]) => {
        const catInfo = CATEGORY_MAP[catKey];
        const isOpen = openCategories.has(catKey);

        return (
          <div key={catKey} className="border border-border/40 rounded-md bg-card/20">
            {/* Category Header */}
            <button
              type="button"
              onClick={() => toggleCategory(catKey)}
              className={`w-full flex items-center justify-between px-4 py-2.5 hover:bg-accent/50 transition-colors rounded-t-md ${isEn ? "text-left" : "text-right"}`}
            >
              <span className="font-heading text-sm font-semibold text-foreground">
                {(isEn ? catInfo.en : catInfo.he)} ({comms.length})
              </span>
              {isOpen ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
              ) : (
                <ChevronLeft className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
              )}
            </button>

            {/* Commentators List */}
            {isOpen && (
              <div className="border-t border-border/40">
                {comms.map((comm) => {
                  const isActive = activeCommentator === comm.name;
                  
                  return (
                    <div key={comm.name} className="border-b border-border/20 last:border-b-0">
                      {/* Commentator Name */}
                      <button
                        type="button"
                        onClick={() => onToggle(comm.name)}
                        data-testid={`commentator-${comm.name.replace(/\s+/g, "-")}`}
                        className={`w-full flex items-center justify-between px-5 py-2 ${isEn ? "text-left" : "text-right"} transition-colors ${
                          isActive
                            ? "bg-gold/10 text-gold font-medium"
                            : "hover:bg-accent/30 text-foreground"
                        }`}
                      >
                        <span className="text-sm font-heading">{isEn ? comm.en : comm.he}</span>
                        <ChevronLeft
                          className={`h-3.5 w-3.5 transition-transform ${
                            isActive ? (isEn ? "-rotate-90 text-gold" : "rotate-90 text-gold") : "text-muted-foreground"
                          }`}
                          strokeWidth={1.5}
                        />
                      </button>

                      {/* Commentary Text */}
                      {isActive && comm.text && (
                        <div className="px-5 py-4 bg-background/50 text-foreground">
                          <div
                            className="text-base leading-relaxed space-y-2"
                            style={{
                              direction: isEn && comm.hasEnglish ? "ltr" : "rtl",
                              unicodeBidi: "embed",
                            }}
                          >
                            {comm.text.split("\n").map((para, idx) => (
                              <p key={idx} className={isEn && comm.hasEnglish ? "text-left" : "text-right"}>
                                {para}
                              </p>
                            ))}
                            {/* English mode without an English translation:
                                a respectful inline notice + the Hebrew is
                                still shown above. */}
                            {isEn && !comm.hasEnglish && (
                              <p
                                className="mt-3 pt-3 border-t border-border/30 text-xs italic text-muted-foreground text-left"
                                dir="ltr"
                                lang="en"
                              >
                                English translation not yet available for this commentary — showing the original Hebrew.
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
